import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { DirectoryContactInlinePicker } from '@/components/treatment/directory';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import type {
  DirectoryHandle,
  DirectoryProviderUnit,
} from '@/hooks/useProviderDirectory';
import type {
  TreatmentRecordRequestChannelType,
  UpdateTreatmentRecordRequestChannelSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import {
  formatDirectoryHandleValue,
  mapHandleTypeToChannelType,
} from '@/utils/providerDirectory';

import { PortalProviderSearch } from '../portal-providers/PortalProviderSearch';
import { RequestMethodSelector } from '../request-details/RequestMethodSelector';

type ExtendedChannelSchema = UpdateTreatmentRecordRequestChannelSchema & {
  portal_provider_id?: string;
};

interface RequestMethodModalProps {
  onClose: () => void;
  matterId: string;
  providerId: string;
  requestId: string;
  zIndex?: number;
  nested?: boolean;
  request: {
    channel?: {
      id?: string;
      type?: TreatmentRecordRequestChannelType;
      email?: string | null;
      fax?: string | null;
      address?: string | null;
      instructions?: string | null;
      portal_record_request_id?: string | null;
      portal_provider_id?: string | null;
      phone_number?: string | null;
      provider_contact_unit_handle_id?: string | null;
    } | null;
  };
  provider: {
    handles?: Array<{ handle_type: string; value: string }>;
    fax?: string | null;
    address?: string | null;
    provider_contact_ref_id?: string | null;
  };
}

export const RequestMethodModal: React.FC<RequestMethodModalProps> = ({
  onClose,
  matterId,
  providerId,
  requestId,
  request,
  provider,
  zIndex,
  nested = false,
}) => {
  const [editedChannel, setEditedChannel] = useState<ExtendedChannelSchema>({});
  const [selectedDirectoryHandle, setSelectedDirectoryHandle] = useState<{
    handle: DirectoryHandle;
    unit: DirectoryProviderUnit;
  } | null>(null);

  const [updateChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();
  const [createChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();

  const providerRefId = provider.provider_contact_ref_id || null;

  // Suggest fax from provider handles or deprecated field
  const suggestedFax = useMemo(() => {
    const fromHandles =
      provider?.handles?.find((h) => h.handle_type === 'FAX')?.value || '';
    const deprecatedField = provider?.fax || '';
    return (fromHandles || deprecatedField).trim();
  }, [provider?.handles, provider?.fax]);

  const suggestedAddress = useMemo(() => {
    const fromHandles =
      provider?.handles?.find((h) => h.handle_type === 'MAILING_ADDRESS')
        ?.value || '';
    const fallback = provider?.address || '';
    return (fromHandles || fallback).trim();
  }, [provider?.handles, provider?.address]);

  useEffect(() => {
    if (request?.channel) {
      const prefilledFax =
        request.channel.fax ||
        (request.channel.type === 'fax'
          ? suggestedFax || undefined
          : undefined);
      const prefilledAddress =
        request.channel.address ||
        (request.channel.type === 'mail'
          ? suggestedAddress || undefined
          : undefined);
      setEditedChannel({
        type: request.channel.type,
        email: request.channel.email,
        fax: prefilledFax,
        address: prefilledAddress,
        instructions: request.channel.instructions,
        portal_record_request_id: request.channel.portal_record_request_id,
        portal_provider_id: request.channel.portal_provider_id || undefined,
      });
    }
  }, [request, suggestedFax, suggestedAddress]);

  // Deterministic channel field prefill helper
  const prefillChannelFields = (
    type: TreatmentRecordRequestChannelType | undefined,
    next: ExtendedChannelSchema,
  ): ExtendedChannelSchema => {
    const t = type || next.type || request?.channel?.type;
    if (t === 'fax') {
      const hasFax = !!(next.fax && next.fax.trim().length > 0);
      if (!hasFax && suggestedFax) {
        next = { ...next, fax: suggestedFax };
      }
    } else if (t === 'mail') {
      const hasAddress = !!(next.address && next.address.trim().length > 0);
      if (!hasAddress && suggestedAddress) {
        next = { ...next, address: suggestedAddress };
      }
    }
    return next;
  };

  const handleDirectorySelect = (
    handle: DirectoryHandle,
    unit: DirectoryProviderUnit,
  ) => {
    setSelectedDirectoryHandle({ handle, unit });

    const targetChannelType = mapHandleTypeToChannelType(
      handle.handle?.handle_type,
    );

    if (targetChannelType) {
      const next = {
        ...editedChannel,
        type: targetChannelType,
        provider_contact_unit_handle_id: String(handle.id),
      } as ExtendedChannelSchema;

      if (targetChannelType === 'fax') {
        next.fax = handle.handle?.value || '';
      } else if (targetChannelType === 'email') {
        next.email = handle.handle?.value || '';
      }

      setEditedChannel(next);
    }
  };

  const handleSave = async () => {
    try {
      if (!request.channel && editedChannel.type) {
        await createChannel({
          matterId,
          providerId,
          requestId,
          createTreatmentRecordRequestChannelSchema: {
            type: editedChannel.type,
            email: editedChannel.email,
            fax: editedChannel.fax,
            address: editedChannel.address,
            instructions: editedChannel.instructions,
            portal_record_request_id: editedChannel.portal_record_request_id,
            portal_provider_id: editedChannel.portal_provider_id,
            phone_number: editedChannel.phone_number,
            provider_contact_unit_handle_id:
              editedChannel.provider_contact_unit_handle_id,
          },
        }).unwrap();
        toast.success('Request method set');
      } else if (request.channel?.id) {
        await updateChannel({
          matterId,
          providerId,
          requestId,
          channelId: request.channel.id,
          updateTreatmentRecordRequestChannelSchema: editedChannel,
        }).unwrap();
        toast.success('Request method updated');
      }
      onClose();
    } catch (error) {
      toast.error('Failed to update request method');
    }
  };

  return (
    <Dialog open onClose={onClose} size="3xl" zIndex={zIndex} nested={nested}>
      <DialogTitle>Set Request Method</DialogTitle>
      <DialogDescription>
        Specify how this request will be submitted to the provider.
      </DialogDescription>
      <DialogBody>
        <div className="space-y-4">
          {/* Directory Contact Selector */}
          {providerRefId && (
            <div>
              <Label>Select from Directory Contacts</Label>
              <div className="mt-2">
                <DirectoryContactInlinePicker
                  providerRefId={providerRefId}
                  selectedHandleId={
                    selectedDirectoryHandle
                      ? String(selectedDirectoryHandle.handle.id)
                      : null
                  }
                  onSelectHandle={handleDirectorySelect}
                  maxVisible={3}
                  showAddButton
                  compact
                />
              </div>
              {selectedDirectoryHandle && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-medium text-gray-900">
                    Selected:{' '}
                    {selectedDirectoryHandle.handle.handle?.name || 'Contact'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {formatDirectoryHandleValue(selectedDirectoryHandle.handle)}
                  </div>
                </div>
              )}
            </div>
          )}

          {!providerRefId && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                Link this provider to the directory to access verified contacts.
              </p>
            </div>
          )}

          <div className="border-t pt-3">
            <Label>Request Method</Label>
            <RequestMethodSelector
              value={editedChannel.type || request.channel?.type}
              onChange={(newType) => {
                let next = {
                  ...editedChannel,
                  type: newType,
                } as ExtendedChannelSchema;
                next = prefillChannelFields(newType, next);
                setEditedChannel(next);
              }}
              className="w-full mt-2"
              inModal
            />
          </div>

          {editedChannel.type === 'portal' && (
            <>
              <PortalProviderSearch
                className="w-full"
                value={editedChannel.portal_provider_id || ''}
                onChange={(portalProviderId) =>
                  setEditedChannel({
                    ...editedChannel,
                    portal_provider_id: portalProviderId,
                  })
                }
                inModal
              />
              <Input
                type="text"
                value={editedChannel.portal_record_request_id || ''}
                onChange={(e) =>
                  setEditedChannel({
                    ...editedChannel,
                    portal_record_request_id: e.target.value,
                  })
                }
                placeholder="Request ID"
                className="w-full text-sm"
              />
            </>
          )}

          {editedChannel.type === 'email' && (
            <Input
              type="email"
              value={editedChannel.email || ''}
              onChange={(e) =>
                setEditedChannel({
                  ...editedChannel,
                  email: e.target.value,
                })
              }
              placeholder="Email address"
              className="w-full text-sm"
            />
          )}

          {editedChannel.type === 'fax' && (
            <Input
              type="tel"
              value={editedChannel.fax || ''}
              onChange={(e) =>
                setEditedChannel({
                  ...editedChannel,
                  fax: e.target.value,
                })
              }
              placeholder="Fax number"
              className="w-full text-sm"
            />
          )}

          {editedChannel.type === 'mail' && (
            <>
              <Label>Mailing Address</Label>
              <textarea
                value={editedChannel.address || ''}
                onChange={(e) =>
                  setEditedChannel({
                    ...editedChannel,
                    address: e.target.value,
                  })
                }
                placeholder="Mailing address"
                className="w-full text-sm px-3 py-2 border border-zinc-950/10 rounded-lg resize-none bg-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 dark:border-white/10 dark:bg-white/5"
                rows={3}
              />
            </>
          )}

          {/* Phone Number Field */}
          {(editedChannel.type === 'fax' ||
            editedChannel.type === 'mail' ||
            editedChannel.type === 'email') && (
            <>
              <Label>Contact Phone Number (Optional)</Label>
              <Input
                type="tel"
                value={editedChannel.phone_number || ''}
                onChange={(e) =>
                  setEditedChannel({
                    ...editedChannel,
                    phone_number: e.target.value,
                  })
                }
                placeholder="Phone number for follow-up"
                className="w-full text-sm"
              />
            </>
          )}

          {editedChannel.type !== 'mail' && (
            <Input
              type="text"
              value={editedChannel.instructions || ''}
              onChange={(e) =>
                setEditedChannel({
                  ...editedChannel,
                  instructions: e.target.value,
                })
              }
              placeholder="Instructions (optional)"
              className="w-full text-sm"
            />
          )}

          {editedChannel.type === 'mail' && (
            <textarea
              value={editedChannel.instructions || ''}
              onChange={(e) =>
                setEditedChannel({
                  ...editedChannel,
                  instructions: e.target.value,
                })
              }
              placeholder="Instructions (optional)"
              className="w-full text-sm px-3 py-2 border border-zinc-950/10 rounded-lg resize-none bg-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 dark:border-white/10 dark:bg-white/5"
              rows={2}
            />
          )}

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} className="flex-1 text-sm py-1.5">
              Save
            </Button>
            <Button outline onClick={onClose} className="flex-1 text-sm py-1.5">
              Cancel
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};
